import { ImageIcon, X } from 'lucide-react';
import { type ChangeEvent, useEffect, useRef, useState } from 'react';

import { cn } from '~shared/shadcn/lib/utils';
import { Button } from '~shared/shadcn/ui/button';
import { Card } from '~shared/shadcn/ui/card';

interface ImageUploaderProps {
  currentImage: string | null;
  onImageChange: (file: File | null) => void;
  maxWidth?: number | string;
  helpText?: string;
  showChangeButtonOverlay?: boolean;
  objectFit?: 'cover' | 'contain' | 'fill';
}

export function ImageUploader({
  currentImage,
  onImageChange,
  maxWidth = 400,
  helpText,
  showChangeButtonOverlay = false,
  objectFit = 'contain',
}: ImageUploaderProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(
    currentImage,
  );
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setSelectedImage(currentImage);
  }, [currentImage]);

  const validateFile = (file: File | null): boolean => {
    if (!file) return false;

    // Check file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    if (!validTypes.includes(file.type)) {
      setError('Only JPG, JPEG, and PNG formats are allowed');
      return false;
    }

    // Check file size (50MB = 50 * 1024 * 1024 bytes)
    if (file.size > 50 * 1024 * 1024) {
      setError('File size exceeds 50MB limit');
      return false;
    }

    setError(null);
    return true;
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;

    if (file && validateFile(file)) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setSelectedImage(event.target?.result as string);
      };
      reader.readAsDataURL(file);
      onImageChange(file);
    } else if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const file = e.dataTransfer.files[0] ?? null;
    if (file && validateFile(file)) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setSelectedImage(event.target?.result as string);
      };
      reader.readAsDataURL(file);
      onImageChange(file);
    }
  };

  const removeImage = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent event from bubbling up
    setSelectedImage(null);
    onImageChange(null);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const triggerFileInput = (e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation(); // Prevent event from bubbling up
    }
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  return (
    <div className='w-full' style={{ maxWidth }}>
      <Card
        className={cn(
          'relative w-full cursor-pointer overflow-hidden border-2 border-dashed transition-colors',
          isDragging
            ? 'border-primary bg-primary/5'
            : 'border-muted-foreground/25',
          selectedImage ? 'border-solid' : 'border-dashed',
          error ? 'border-destructive' : '',
        )}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={selectedImage ? undefined : triggerFileInput}
      >
        <div className='relative h-[120px] w-full sm:h-[160px] md:h-[200px] lg:h-[400px]'>
          {selectedImage ? (
            <>
              <div className='absolute inset-0 flex items-center justify-center bg-muted/10'>
                <img
                  src={selectedImage || '/placeholder.svg'}
                  alt='Selected image'
                  className={`max-h-full max-w-full ${
                    objectFit === 'cover'
                      ? 'size-full object-cover'
                      : objectFit === 'fill'
                        ? 'size-full'
                        : 'object-contain'
                  }`}
                />
              </div>
              <div className='absolute right-2 top-2 z-10 flex gap-2'>
                <Button
                  variant='destructive'
                  size='icon'
                  className='size-7 rounded-full opacity-90 transition-opacity hover:opacity-100 sm:size-8'
                  onClick={removeImage}
                  type='button'
                >
                  <X className='size-3 sm:size-4' />
                </Button>
                {showChangeButtonOverlay && (
                  <Button
                    variant='secondary'
                    size='sm'
                    className='h-7 rounded-full px-2 opacity-90 transition-opacity hover:opacity-100 sm:h-8 sm:px-3'
                    onClick={triggerFileInput}
                    type='button'
                  >
                    <ImageIcon className='mr-1 size-3 sm:size-4' />
                    <span className='text-xs'>Select</span>
                  </Button>
                )}
              </div>
            </>
          ) : (
            <div className='absolute inset-0 flex flex-col items-center justify-center gap-1 p-2 text-center sm:gap-2 sm:p-4'>
              <img
                src='https://storage.googleapis.com/studio-class-default-images/upload_logo.jpeg'
                alt='Upload logo placeholder'
                className='size-40 rounded-lg object-cover'
              />
              <div>
                <p className='text-xs font-medium sm:text-sm'>
                  Click to upload or drag and drop
                </p>
                <p className='text-[10px] text-muted-foreground sm:text-xs'>
                  {helpText ?? 'JPG, JPEG, or PNG only (max. 50MB)'}
                </p>
              </div>
            </div>
          )}
        </div>
      </Card>

      {error && <p className='mt-1 text-xs text-destructive'>{error}</p>}

      {selectedImage && !showChangeButtonOverlay && (
        <div className='mt-2 flex justify-end gap-2'>
          <Button
            variant='outline'
            size='sm'
            onClick={triggerFileInput}
            className='text-xs'
            type='button'
          >
            <ImageIcon className='mr-2 size-3' />
            Select New
          </Button>
        </div>
      )}

      <input
        type='file'
        ref={fileInputRef}
        onChange={handleFileChange}
        accept='image/jpeg,image/jpg,image/png'
        className='hidden'
      />
    </div>
  );
}
